import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiRefreshCw, FiAlertTriangle } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import ATable from '../../organisms/ATable';
import Loading from '../../ions/Loading';
import NewTooltip from '../../atoms/NewTooltip';
import PismoStatusBadge from './shared/PismoStatusBadge';
import BatchStatusBadge from './shared/RegistrationBatchStatusBadge';
import { formatCurrency, formatDate, formatCpfCnpj } from '../../../utils/collateralFormatters';
import useCreditRightBatchPageView from '../../../hooks/useCreditRightBatchPageView';

const ERROR_STATUSES = ['REJECTED', 'FAILED_RETRYABLE', 'FAILED_PERMANENT'];

const columns = [
    { id: 'internalNumber', title: 'Nº interno', field: 'internalNumber', canSort: false, width: '160px' },
    { id: 'debtor', title: 'Sacado', field: 'debtorName', canSort: false },
    { id: 'nfe', title: 'NFe / Título', field: 'nfe', canSort: false, width: '160px' },
    { id: 'faceValue', title: 'Valor', field: 'faceValue', canSort: false, cssClass: 'text-right', width: '120px' },
    { id: 'maturity', title: 'Vencimento', field: 'maturityDate', canSort: false, width: '110px' },
    { id: 'status', title: 'Status', field: 'status', canSort: false, cssClass: 'text-center', width: '150px' },
];

const SummaryCard = ({ label, value, tone = '' }: { label: string; value: any; tone?: string }) => (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700 px-4 py-2 bg-white dark:bg-slate-800">
        <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
        <div className={`text-lg font-semibold ${tone || 'text-gray-900 dark:text-white'}`}>{value}</div>
    </div>
);

const CreditRightBatchPageView = () => {
    const navigate = useNavigate();
    const { batch, isLoading, resending, resendFailed, hasResendableItems, refresh } = useCreditRightBatchPageView();

    if (isLoading || !batch) return <Loading />;

    const items = batch.items || [];
    const hasFailures = (batch.failureCount || 0) > 0;

    return (
        <div className="flex flex-col h-full bg-gray-50 dark:bg-slate-900">
            <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('/collateral/credit-right-batches')}
                            className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            title="Voltar aos lotes">
                            <FiArrowLeft size={18} />
                        </button>
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Lote {batch.id}
                                <span className="ml-2"><BatchStatusBadge status={batch.status} statusFormatted={batch.statusFormatted} /></span>
                            </h1>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                Contrato {batch.contractNumber || '—'} · Operação {batch.operationNumber || '—'} · {batch.assignorName || '—'} · {batch.createdAt || '—'}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={resendFailed}
                            disabled={resending || !hasResendableItems}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            title={hasResendableItems ? 'Reenviar as duplicatas que falharam neste lote' : 'Nenhuma duplicata deste lote pode ser reenviada'}>
                            <FiRefreshCw size={14} />
                            {resending ? 'Reenviando...' : 'Reenviar falhas'}
                        </button>
                        <button onClick={refresh}
                            className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            title="Atualizar lote">
                            <IoIosRefresh size={18} />
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3 max-w-xl">
                    <SummaryCard label="Total" value={batch.totalCount} />
                    <SummaryCard label="Sucessos" value={batch.successCount} tone="text-green-700 dark:text-green-400" />
                    <SummaryCard label="Falhas" value={batch.failureCount} tone={hasFailures ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'} />
                </div>
            </div>

            <div className="flex-1 overflow-auto p-3 [&_table]:table-fixed [&_table]:w-full">
                <ATable headers={columns}>
                    {items.length === 0 ? (
                        <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhuma duplicata neste lote</td></tr>
                    ) : items.map((row: any, index: number) => (
                        <tr key={row.id}
                            className={`${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-[11px] font-mono dark:text-white truncate" title={row.internalNumber}>{row.internalNumber || '—'}</td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white truncate" title={row.debtorName}>
                                <div>{row.debtorName || '—'}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{formatCpfCnpj(row.debtorDocument)}</div>
                            </td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white truncate" title={row.nfe}>
                                <div className="font-mono truncate">{row.nfe || '—'}</div>
                                <div className="text-gray-500 dark:text-gray-400">Título {row.documentTitle || '—'} / Parc. {row.installmentNumber ?? '—'}</div>
                            </td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatCurrency(row.faceValue)}</td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white">{row.maturityDate ? formatDate(row.maturityDate) : '—'}</td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                <div className="flex items-center justify-center gap-1">
                                    <PismoStatusBadge status={row.status} statusFormatted={row.statusFormatted} />
                                    {ERROR_STATUSES.includes(row.status) && (
                                        <NewTooltip content={<span className="block w-64 whitespace-normal break-words text-left">{row.errorMessage || row.errorCodeFormatted || row.errorCode || 'Erro no registro'}</span>} alignment="left">
                                            <button
                                                type="button"
                                                className="flex-shrink-0 rounded focus:outline-none focus:ring-2 focus:ring-red-400"
                                                aria-label={`Erro no registro: ${row.errorMessage || row.errorCodeFormatted || row.errorCode || 'erro no registro'}`}>
                                                <FiAlertTriangle className="text-red-500" size={14} />
                                            </button>
                                        </NewTooltip>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </ATable>
            </div>
        </div>
    );
};

export default CreditRightBatchPageView;
